/**
 * A short memo with in-flight sharing for the menu's list calls (SC-477): every menu visit, the
 * booking form and the pickers each ask for the same few lists, and each ask costs several
 * Back4App operations.
 *
 * Tenant safety: an entry is served only to the user who loaded it. The scope is the signed-in
 * user's id, read on every call, so a sign-out (no user) or an account change empties the memo
 * and never lets one account see another's list. The company is part of each call's key where
 * the list depends on it. Without a signed-in user nothing is memoised at all.
 */
import Parse from '../initializeParse'
import { parseUserId } from '../../utils/parseUserId'

export const LIST_MEMO_TTL_MS = 60_000

interface Entry<T> {
  promise: Promise<T>
  /** Set once the load succeeded; failures are dropped so the next call retries. */
  expiresAt: number | null
}

export interface ListMemo {
  /** Memoised load: one call per `key` per TTL, and concurrent callers share the same call. */
  get<T>(key: string, load: () => Promise<T>, options?: { fresh?: boolean }): Promise<T>
  /** Forget everything, including loads in flight (they finish for their callers but stay unstored). */
  clear(): void
  /** Entries held now (loads in flight and answers within their TTL); lets a test see what stays in memory. */
  size(): number
  /**
   * For a write that changes the listed data: returns the write's own result or error, and clears
   * the memo once it settles, whether or not it succeeded (a failed write may still have changed
   * something, so the next read goes to the server).
   */
  afterWrite<T>(write: Promise<T>): Promise<T>
}

export function createListMemo(ttlMs: number = LIST_MEMO_TTL_MS): ListMemo {
  let entries = new Map<string, Entry<unknown>>()
  let scope: string | null = null

  const clear = () => {
    // A new Map, not entries.clear(): a load still in flight checks that its own entry is the
    // one stored, so it can never put a pre-clear answer back.
    entries = new Map()
  }

  // A window event, so a memo outlives neither a forced sign-out nor an expired session.
  if (typeof window !== 'undefined' && typeof window.addEventListener === 'function') {
    window.addEventListener('sessionExpired', clear)
  }

  return {
    clear,
    size: () => entries.size,
    async afterWrite<T>(write: Promise<T>): Promise<T> {
      try {
        return await write
      } finally {
        clear()
      }
    },
    get<T>(key: string, load: () => Promise<T>, options?: { fresh?: boolean }): Promise<T> {
      const userId = parseUserId(Parse)
      if (userId !== scope) {
        clear()
        scope = userId
      }
      if (!userId) return load()

      const now = Date.now()
      // Drop answers past their TTL so a key that carries a changing value (the bookings refresh
      // trigger) cannot pile up one dead entry per change. Loads in flight (expiresAt null) stay.
      for (const [k, e] of entries) {
        if (e.expiresAt !== null && e.expiresAt <= now) entries.delete(k)
      }
      const hit = entries.get(key) as Entry<T> | undefined
      if (hit && !options?.fresh && (hit.expiresAt === null || hit.expiresAt > now)) {
        return hit.promise
      }

      const own = entries
      const promise = load()
      const entry: Entry<T> = { promise, expiresAt: null }
      own.set(key, entry)
      promise.then(
        () => {
          if (own.get(key) !== entry) return
          // TTL 0 only shares a read in flight: an entry kept after the answer is dead weight,
          // and keys that carry a changing value (bookings refresh trigger) would pile up.
          if (ttlMs === 0) own.delete(key)
          else entry.expiresAt = Date.now() + ttlMs
        },
        () => {
          if (own.get(key) === entry) own.delete(key)
        }
      )
      return promise
    },
  }
}
